import { HTTPMETHOD } from '../enums/HTTPMETHOD.js';
import PAYLOAD from './DOM/form/PAYLOAD.js';
/** A Map like object, representative of an Element Model */
export default class Model {
    // public className: string;
    attributes;
    data;
    meta;
    /** Constructs a generic MODEL */
    constructor(attributes = {}, data = {}, meta = {}) {
        this.attributes = attributes;
        this.data = data;
        this.meta = meta;
    }
    /** Sets a property (or a collection of properties) for this MODEL
        @param key Name of property || An object containing key/value pairs
        @param value Value for property
        @param returnThis Optionally return the given value or self if true (default)
        @param allowEmpty If true (default) empty values can be added
    */
    set(key, value, returnThis = true, allowEmpty = true) {
        if (typeof key === 'string') {
            try {
                this[key] = typeof value === 'undefined' ? key : value;
                return returnThis ? this : value;
            }
            catch (e) {
                console.log('Unable to set property of this MODEL');
                throw e;
            }
        }
        for (let prop in key) {
            if (typeof prop === 'string') {
                if (allowEmpty || key[prop] !== 'undefined') {
                    this[prop] = key[prop];
                }
            }
        }
        return returnThis ? this : value;
    }
    /** Appends/Overrides property (or a collection of properties) for the specified Attributes collection
        @description Similar to right join, new values override existing
        @param attrName Attribute Collection Name
        @param obj An object containing key/value pairs
        @param allowEmpty If true (default) empty values can be added
    */
    append(attrName, obj, allowEmpty = true) {
        for (let prop in obj) {
            if (typeof prop === 'string') {
                if (allowEmpty || obj[prop] !== 'undefined') {
                    this[attrName][prop] = obj[prop];
                }
            }
        }
        return this;
    }
    remove(key) {
        try {
            if (typeof this[key] !== 'undefined') {
                delete this[key];
            }
            else {
                console.warn(`Unable to remove property "${key}" from this MODEL`, this);
            }
        }
        catch (e) {
            console.warn(`Unable to remove property "${key}" from this MODEL`, this, e);
        }
        return this;
    }
    /** Throws an error if given value is not defined
        @param value Value
        @returns Verified required value
        @throws {Error} Missing Value
    */
    required(value) {
        if (typeof value === 'undefined') {
            console.error('Unable to set required value', this, value);
            throw new Error('Missing required value');
        }
        else {
            return value;
        }
    }
    getAttribute(key) {
        if (key === undefined) {
            return this.attributes ?? {};
        }
        return this.attributes?.[key];
    }
    setAttribute(key, value) {
        if (!this.attributes) {
            this.attributes = {};
        }
        this.attributes[key] = value;
        return this;
    }
    getData(key) {
        if (key === undefined) {
            return this.data;
        }
        return this.data[key];
    }
    setData(key, value) {
        this.data[key] = value;
        return this;
    }
    getMeta(key) {
        if (key === undefined) {
            return this.meta;
        }
        return this.meta[key];
    }
    /** Sets a property (or a collection of properties) for this Model's META */
    setMeta(key, value) {
        this.meta[key] = value;
        return this;
    }
    /** Returns a string representation of this MODEL */
    toString() {
        return this.constructor.name + '()';
    }
    /** Get a property of this Model */
    get(key) {
        try {
            return this[key];
        }
        catch (e) {
            console.log(`Unable to get property "${key}" of this MODEL.`, e);
        }
    }
    /** Generates an URL friendly parameter string for use in GET requests
        @param params Parameter Object
        @returns URL Parameter String
    */
    getParamsAsUrlParams(params) {
        return Object.keys(params).map((key) => key + '=' + encodeURIComponent(params[key])).join('&');
    }
    /** Performs an AJAX request and performs the given callback (fn) with the response
        @param url HTTP Request Url
        @param fn Function that accepts the resulting payload as its only argument
        @param method Request Method (ie: 'POST','GET')
        @param username Basic Auth Username
        @param password Basic Auth Password or Bearer Token
        @param json JSON
    */
    getJson(url, fn, method = HTTPMETHOD.GET, username, password, json) {
        let xhr = new XMLHttpRequest();
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                let payload;
                try {
                    payload = JSON.parse(xhr.responseText);
                }
                catch (error) {
                    payload = { error: error, raw: xhr.responseText };
                }
                fn(payload, xhr.status);
            }
        };
        xhr.open(method, url, true);
        // Basic Auth
        if (typeof username === 'string' && typeof password === 'string') {
            xhr.setRequestHeader('Authorization', 'Basic ' + btoa(unescape(encodeURIComponent(username + ':' + password))));
            xhr.withCredentials = true;
        }
        // Bearer Token
        if (typeof username === 'undefined' && typeof password === 'string') {
            xhr.setRequestHeader('Authorization', 'Bearer ' + password);
            xhr.withCredentials = true;
        }
        // When POSTing, specify content type and pass the JSON
        if (method === HTTPMETHOD.POST) {
            xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
            xhr.send(JSON.stringify(json));
        }
        // When using GET, just fire it off
        if (method === HTTPMETHOD.GET) {
            xhr.send();
        }
    }
    /** Retrieves a Payload matching the given params (if permitted)
        @param url API Endpoint Url
        @returns Promise to resolve a PAYLOAD Class
    */
    getPayload(url) {
        console.log('GET PAYLOAD', url);
        return new Promise((resolve, reject) => {
            try {
                this.getJson(url, (payload) => resolve(new PAYLOAD(payload)));
            }
            catch (e) {
                console.warn('Unable to retrieve payload', url, e);
                reject(e);
            }
        });
    }
}
